"use client"

import { useRef } from "react"
import Link from "next/link"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { formatProductTitle } from "@/lib/breadcrumb-utils"
import { getProductPath as canonicalGetProductPath } from "@/lib/urls"
import { resolveBadgesForRow } from "@/lib/badges"
import { formatCents } from "@/lib/format-utils"
import { isRenderableImageUrl } from "@/lib/proxied-storage-path"
import type { BestRefrigeratorsRanking } from "@/lib/best-refrigerators/ranking"
import { StarIcon } from "@/components/brand-kit/icon-sprite"
import { SafeImage } from "@/components/ui/safe-image"
import { canOptimizeImageSrc } from "@/lib/image-optimizable"

// ─── Page width constant ────────────────────────────────────────────────────
export const PAGE_W = "max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8"


// ─── Types ──────────────────────────────────────────────────────────────────
export interface HubFeature {
  icon: string
  title: string
  description: string
}

export interface HubFAQ {
  question: string
  answer: string
}

export interface HubBrand {
  name: string
  features: string[]
  link: string
  logo?: string
  /** FRIDGE-1119: set server-side when the brand's collection does not render (FRIDGE-1420: or its brand page does not serve) — the tile shows, unlinked. */
  unlinked?: boolean
}

export interface HubTool {
  href: string
  title: string
  description: string
}

export interface HubData {
  id: number
  slug: string
  title: string
  hub_type: string
  description: string | null
  features: HubFeature[]
  faqs: HubFAQ[]
  brands_section: HubBrand[]
  tools_section: HubTool[]
  collection_slug: string | null
  cta_title: string | null
  cta_description: string | null
  cta_button_text: string | null
  cta_button_link: string | null
  featured_image: string | null
  /**
   * FRIDGE-1119: the hub's own collection link, decided server-side
   * (app/[hub-slug]/page.tsx): the collection when it renders, its one-hop edge
   * destination when it redirects, null when it must not be linked. Undefined =
   * not decided (the read failed) → the legacy `/collections/<slug>`.
   */
  collection_href?: string | null
  /** FRIDGE-1434: the head-term H1 and the answer-first sentence (lib/seo/head-term-headlines). */
  headline?: string | null
  answer_first?: string | null
}

/** FRIDGE-1119: the hub's own collection link, or null when it must not render. */
/**
 * FRIDGE-1400: the hub hero photo, the largest paint of a category, feature or
 * specialty hub on a phone. It was a plain img at the source's own size (the
 * category fallback is a 800 px Unsplash JPEG) found only once layout ran; now
 * it goes through the image optimizer when its host allows (an AVIF sized to
 * the slot) and is fetched first. A source the optimizer refuses stays a plain
 * img, also fetched first. The parent must be positioned (it fills it).
 */
export function HubHeroImage({ src, alt }: { src: string; alt: string }) {
  if (canOptimizeImageSrc(src)) {
    return (
      <SafeImage
        src={src}
        alt={alt}
        fill
        sizes="(min-width: 1024px) 50vw, 100vw"
        fetchPriority="high"
        loading="eager"
        className="object-cover"
      />
    )
  }
  // eslint-disable-next-line @next/next/no-img-element -- a host next/image may not optimize
  return <img src={src} alt={alt} fetchPriority="high" loading="eager" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
}

export function hubCollectionHref(hub: Pick<HubData, "collection_href" | "collection_slug" | "slug">): string | null {
  if (hub.collection_href === null) return null
  return hub.collection_href ?? `/collections/${hub.collection_slug || hub.slug}`
}

/** FRIDGE-1119: a brand tile's link, or null when its collection (FRIDGE-1420: or brand page) does not render. */
export function hubBrandHref(brand: HubBrand): string | null {
  if (brand.unlinked) return null
  return brand.link || `/collections/${brand.name.toLowerCase().replace(/\s+/g, "-")}`
}

/** A Link when there is somewhere live to go, the same box without one. */
export function MaybeLink({ href, className, children }: { href: string | null; className?: string; children: React.ReactNode }) {
  return href ? <Link href={href} className={className}>{children}</Link> : <div className={className}>{children}</div>
}

/**
 * Hero intro sentence — keyword-placement standard (plan item 15). Every hub
 * variant must render a first sentence that leads with its head term
 * (`hub.title`). All 61 active hubs carry a curated `description` today
 * (verified 2026-07-18), so this fallback normally never fires; it guarantees
 * the first-sentence placement still holds if a new hub ships without one, and
 * states only what the page does — cross-retailer comparison over the models
 * listed below. Head term leads so the standard is satisfied by construction.
 */
export function hubHeroIntro(hub: Pick<HubData, "title" | "description">): string {
  return (
    hub.description?.trim() ||
    `${hub.title}: compare prices, specs, and top-rated models across major online appliance retailers.`
  )
}

export interface Product {
  id: string
  title: string
  brand: string
  model: string
  normalized_model: string
  seo_slug: string
  images: string[]
  canonical_url: string
  lowest_price_cents: number
  average_price_30d_cents: number
  lowest_price_ever_cents?: number
  type: string
  features: string[]
  specs_json: any
  created_at: string
  /** FRIDGE-1485: fin_review_rollup.weighted_rating (0.1 steps) when the FIN carries MIN_REVIEWS_FOR_RATING or more reviews; null otherwise. Never invented. */
  rating?: number | null
  /** FRIDGE-1485: the reviews behind `rating`; null with it. */
  reviews?: number | null
}

export interface Article {
  /** FRIDGE-1086 — the author's photo for the card byline (resolved in page.tsx). */
  authorImageUrl?: string | null
  id: number
  title: string
  slug: string
  image: string | null
  published_at: string
  content?: string
  author_name?: string
}

export interface RelatedHub {
  slug: string
  title: string
  hub_type: string
  description: string | null
}

export interface BrandPeer {
  name: string
  /** The live brand page the tile links (FRIDGE-1420: its own, or the one its redirect lands on). */
  slug: string
  logo: string
  /** FRIDGE-1420: false when the brand has no live page — the tile shows, unlinked. */
  linkable?: boolean
}

export interface HubClientProps {
  hub: HubData
  products: Product[]
  articles: Article[]
  relatedHubs?: RelatedHub[]
  brandPeers?: BrandPeer[]
  /** FRIDGE-1481: the sourced tables for /best-refrigerators (lib/best-refrigerators/ranking); null on every other hub. */
  ranking?: BestRefrigeratorsRanking | null
}

// ─── Icon name → emoji mapping (for hub features stored as Lucide names) ───
// Covers every icon name present in hub_pages.features (DB-verified 07/16/2026).
// Render sites must NEVER fall back to the raw name — use resolveFeatureEmoji.
export const iconNameToEmoji: Record<string, string> = {
  Thermometer: '🌡️', Snowflake: '❄️', Shield: '🛡️', Leaf: '🍃',
  Package: '📦', Zap: '⚡', Ruler: '📏', DollarSign: '💲',
  Settings: '⚙️', Home: '🏠', Droplets: '💧', Wind: '💨',
  Lock: '🔒', Star: '⭐', Clock: '🕐', Wrench: '🔧',
  Volume2: '🔊', VolumeX: '🔇', Battery: '🔋', Wifi: '📶',
  Sun: '☀️', Moon: '🌙', Heart: '❤️', Check: '✅',
  AlertTriangle: '⚠️', Info: 'ℹ️', Truck: '🚚', Scale: '⚖️',
  Maximize: '📐', Minimize: '📐', Gauge: '🎯', Lightbulb: '💡',
  Layers: '📚', Eye: '👁️', Target: '🎯', Award: '🏆',
  Maximize2: '📐', Minimize2: '📐', RefreshCw: '🔄', DoorOpen: '🚪',
  Building2: '🏢', Wine: '🍷', Beer: '🍺', Box: '📦', Cpu: '🖥️',
  ShoppingCart: '🛒', Sparkles: '✨', GlassWater: '🥛', Square: '⬜',
  Palette: '🎨', Users: '👥', Factory: '🏭', GraduationCap: '🎓',
  LayoutGrid: '🔲', Gem: '💎', Flame: '🔥', Flag: '🚩',
  Paintbrush: '🖌️', ChefHat: '👨‍🍳', Move: '↔️', Mic: '🎙️',
  Accessibility: '♿', Circle: '⭕', CheckCircle: '✅', ArrowDown: '⬇️',
  TrendingUp: '📈', AlignHorizontalSpaceAround: '↔️', ArrowUp: '⬆️',
  Calculator: '🧮', Camera: '📷', Bell: '🔔', Warehouse: '🏬',
  Hexagon: '⬡', Crown: '👑', CloudRain: '🌧️', Smartphone: '📱',
  Columns: '🔳', LayoutPanelLeft: '🔲',
}

/** Emoji for a feature icon value. Never returns a raw Lucide NAME as text:
 *  unknown values render as themselves only if they actually are emoji
 *  (non-ASCII), else the neutral 📋. */
export function resolveFeatureEmoji(icon: string | null | undefined): string {
  if (!icon) return '📋'
  if (iconNameToEmoji[icon]) return iconNameToEmoji[icon]
  return /[^\x20-\x7e]/.test(icon) ? icon : '📋'
}

// ─── Strip leading "Best" from hub titles to avoid "Top Best Freezers" ─────
export function stripBestPrefix(title: string): string {
  return title.replace(/^Best\s+/i, '')
}

// ─── Utility functions ──────────────────────────────────────────────────────
// FRIDGE-1485: the id-hash "rating" generator that lived here is gone. A hub
// row's rating and review count come from the review rollup (hub-ratings.ts)
// or are null, and a null renders nothing.

// FRIDGE-1505: formatted in UTC, the way the server renders it. In local time
// a US browser read 'September 19' where the server said 'September 20' for
// any timestamp before 07:00/08:00 UTC, and the mismatch threw React #418
// during hydration on every root hub with article cards.
export const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

// ─── Consideration → hub/tool link resolver (Option C: hub first, tool fallback)
// Static keyword map covers the most common consideration themes across all guides.
// Dynamic matching against relatedHubs catches anything the static map misses.
const CONSIDERATION_HUB_MAP: Record<string, string> = {
  // Energy / efficiency keywords
  'energy': '/energy-efficient-refrigerators', 'efficient': '/energy-efficient-refrigerators',
  'energy star': '/energy-efficient-refrigerators', 'kwh': '/energy-efficient-refrigerators',
  'eco': '/energy-efficient-refrigerators', 'inverter': '/energy-efficient-refrigerators',
  // Size / capacity
  'size': '/large-capacity-refrigerators', 'capacity': '/large-capacity-refrigerators',
  'sizing': '/large-capacity-refrigerators', 'large': '/large-capacity-refrigerators',
  'family': '/large-capacity-refrigerators',
  // Compact / apartment
  'compact': '/apartment-size-refrigerators', 'apartment': '/apartment-size-refrigerators',
  'small': '/mini-fridges', 'mini': '/mini-fridges',
  // Noise
  'noise': '/quiet-refrigerators', 'quiet': '/quiet-refrigerators', 'sound': '/quiet-refrigerators',
  // Smart / tech
  'smart': '/smart-fridges', 'wifi': '/smart-fridges', 'technology': '/smart-fridges',
  // Style / type
  'french door': '/french-door-refrigerators', 'side by side': '/side-by-side-refrigerators',
  'bottom freezer': '/bottom-freezer-refrigerators', 'top freezer': '/top-freezer-refrigerators',
  'counter depth': '/counter-depth-refrigerators', 'counter-depth': '/counter-depth-refrigerators',
  'built-in': '/built-in-refrigerators', 'built in': '/built-in-refrigerators',
  // Luxury / premium
  'luxury': '/luxury-refrigerators', 'premium': '/luxury-refrigerators',
  // Freezer
  'freezer': '/best-freezers', 'chest': '/best-freezers', 'upright': '/best-freezers',
  // Specialty
  'garage': '/garage-refrigerators', 'outdoor': '/outdoor-refrigerators',
  'wine': '/wine-beverage-refrigerators', 'beverage': '/wine-beverage-refrigerators',
  'commercial': '/commercial-refrigerators', 'ice maker': '/ice-makers',
  // Brands
  'brand': '/best-refrigerators', 'brands': '/best-refrigerators',
  // Comparisons
  'compare': '/refrigerator-comparisons', 'comparison': '/refrigerator-comparisons',
}

/**
 * Resolves a Key Consideration card to the best internal link.
 * Priority: static hub map → dynamic relatedHubs match → tool match → null
 */
export function resolveConsiderationHref(
  featureTitle: string,
  relatedHubs: RelatedHub[],
  tools: HubTool[],
): { href: string; label: string } | null {
  const titleLower = featureTitle.toLowerCase()

  // Cost tasks should open the existing calculator, not a shopping category.
  if (/^energy (?:use|costs?)$/i.test(featureTitle.trim())) {
    const calculator = tools.find(tool => tool.href === '/tools/energy-cost-calculator' || tool.href === 'https://fridge.com/tools/energy-cost-calculator')
    if (calculator) return { href: '/tools/energy-cost-calculator', label: 'Estimate electricity cost' }
  }

  // 1. Static hub map — check multi-word keys first, then single-word
  const multiWordKeys = Object.keys(CONSIDERATION_HUB_MAP).filter(k => k.includes(' '))
  for (const key of multiWordKeys) {
    if (titleLower.includes(key)) return { href: CONSIDERATION_HUB_MAP[key], label: 'Learn more' }
  }
  const singleWordKeys = Object.keys(CONSIDERATION_HUB_MAP).filter(k => !k.includes(' '))
  for (const key of singleWordKeys) {
    if (titleLower.includes(key)) return { href: CONSIDERATION_HUB_MAP[key], label: 'Learn more' }
  }

  // 2. Dynamic match against relatedHubs titles
  const titleWords = titleLower.split(/\s+/).filter(w => w.length > 3)
  for (const rh of relatedHubs) {
    const rhLower = rh.title.toLowerCase()
    if (titleWords.some(w => rhLower.includes(w))) {
      return { href: `/${rh.slug}`, label: 'Explore' }
    }
  }

  // 3. Tool fallback — match feature keywords against tool titles
  for (const tool of tools) {
    const toolLower = tool.title.toLowerCase()
    if (titleWords.some(w => toolLower.includes(w))) {
      return { href: tool.href, label: 'Try the tool' }
    }
  }

  return null
}

// Canonical display rule (owner ruling 07/25/2026): whole dollars drop ".00",
// real cents render exactly. Shared implementation in lib/format-utils.
export function formatPrice(cents: number) {
  return formatCents(cents)
}

export { formatProductTitle }

// ─── Display product transformation ─────────────────────────────────────────
export function transformProducts(products: Product[]) {
  return products
    // FRIDGE-349: the hub data layer now returns the mirrored photo as a
    // site-relative /product-images/… path; the old `/^https?:/` test would
    // have dropped every mirrored product from the grid.
    .filter(p => Array.isArray(p.images) && p.images.length > 0 && isRenderableImageUrl(p.images[0]))
    .map(p => ({
      ...p,
      price: p.lowest_price_cents ? p.lowest_price_cents / 100 : 0,
      originalPrice: p.average_price_30d_cents ? p.average_price_30d_cents / 100 : 0,
      // FRIDGE-1485: the rollup figures the server attached, or null. Never a
      // fallback — specs_json.rating is null on every active row and was never
      // a source, and a number derived from the id is not a rating.
      rating: typeof p.rating === 'number' && Number.isFinite(p.rating) ? p.rating : null,
      reviews: typeof p.reviews === 'number' && Number.isFinite(p.reviews) ? p.reviews : null,
    }))
}

export type DisplayProduct = ReturnType<typeof transformProducts>[0]

/**
 * FRIDGE-278 — no badge parameters. Every hub call site used to pass a badge
 * as two loose strings ("Trending", "bg-green-600"), and those strings had
 * been inert since this function started setting `badges`: ProductCarouselCard
 * takes the canonical branch whenever `badges` is defined, and
 * resolveBadgesForRow always returns an array. Inert but not harmless — one of
 * them still read green-600 after FRIDGE-141 moved every Trending badge to
 * green-700, so the file looked like a live contrast bug and was a ready
 * template for the next hand-written pill. The only way to give a hub card a
 * badge now is the canonical set below.
 */
export function toCarouselProduct(p: DisplayProduct) {
  return {
    brand: p.brand,
    title: formatProductTitle(p.title),
    price: p.lowest_price_cents > 0 ? formatPrice(p.lowest_price_cents) : "",
    image: p.images[0],
    // FRIDGE-1185: the card cross-fades to the first distinct curated photo on
    // mouse hover (components/brand-kit/card-image.tsx). Without the list it
    // has no second photo to show — every hub rail card was hover-dead on
    // production (09/28/2026: 1 of 30 Avanti hub cards, though 42 of 67 active
    // Avanti products hold two or more mirrored photos).
    images: p.images,
    href: getProductUrl(p),
    rating: p.rating ?? undefined,
    reviews: p.reviews ?? undefined,
    // Canonical badge cut-over (docs/Page-Types-And-Templates.md §6): resolve the
    // canonical set from the REAL row columns (lowest/avg-30d/ever price,
    // specs_json, created_at — never the hash-fallback rating above). Passing
    // the field switches ProductCarouselCard into canonical mode:
    // Real-or-Hidden, the fabricated rail-label pill is retired on hub cards.
    badges: resolveBadgesForRow(p),
  }
}

export function getProductUrl(product: Product | DisplayProduct): string {
  // Route through the canonical helper. Falls back to '#' (no-op anchor) if
  // the record has no resolvable identifier — better than emitting
  // `/products/undefined` (the 2026-05-08 bug class). Hub callers use this
  // value as `<Link href={...}>` which doesn't accept null cleanly, so we
  // keep the string return type and let callers use the original `id` for
  // a 404 page if they really want.
  const path = canonicalGetProductPath(product as any)
  if (path) return path
  // Last-resort fallback: `/products/<id>` (UUID). Routes to 404 today since
  // the page route looks up by slug/model only, but at least keeps the URL
  // shape parseable instead of producing the literal token "undefined".
  if (product.id && typeof product.id === 'string') return `/products/${product.id}`
  return '#'
}

// ─── Carousel sorts ─────────────────────────────────────────────────────────
// FRIDGE-1485: no rail is ordered by an invented number. "Featured" keeps the
// hub's stored order (priced rows first); "Top-Rated" holds only rows that
// carry a rollup rating; "rated first" is the editor's-picks order for the
// guide and comparison rails — rated rows by rating, then the rest as stored.
const pricedFirst = (a: DisplayProduct, b: DisplayProduct) => {
  const aHasPrice = a.price > 0
  const bHasPrice = b.price > 0
  return aHasPrice === bHasPrice ? 0 : aHasPrice ? -1 : 1
}

export function getFeaturedProducts(products: DisplayProduct[], count = 8) {
  return [...products].sort(pricedFirst).slice(0, count)
}

export function getTopRatedProducts(products: DisplayProduct[], count = 8) {
  return products
    .filter((p): p is DisplayProduct & { rating: number } => typeof p.rating === 'number')
    .sort((a, b) => pricedFirst(a, b) || b.rating - a.rating || (b.reviews ?? 0) - (a.reviews ?? 0) || a.price - b.price)
    .slice(0, count)
}

export function getRatedFirstProducts(products: DisplayProduct[], count = 8) {
  const rated = getTopRatedProducts(products, products.length)
  const seen = new Set(rated.map((p) => p.id))
  return [...rated, ...getFeaturedProducts(products, products.length).filter((p) => !seen.has(p.id))].slice(0, count)
}

export function getBestValueProducts(products: DisplayProduct[], count = 8) {
  // One card per model: the same model can reach a hub as several rows (finishes,
  // listings) and the root hub showed one Verona three times at three prices
  // (FRIDGE-1031). Sorted ascending first, so the survivor is the lowest price.
  const seen = new Set<string>()
  return [...products]
    .filter(p => p.price > 0)
    .sort((a, b) => a.price - b.price)
    .filter(p => {
      const key = (p.normalized_model || p.model || String(p.id)).toString().trim().toUpperCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .slice(0, count)
}

// ─── Shared components ──────────────────────────────────────────────────────
export function PageSectionHeader({ eyebrow, title, actions }: { eyebrow: string; title: string; actions?: React.ReactNode }) {
  return (
    <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-6">
      <div>
        <span className="text-[#ff9951] font-bold tracking-widest text-xs mb-2 block uppercase">{eyebrow}</span>
        <h2 className="text-2xl md:text-3xl font-black tracking-tighter leading-tight text-gray-900">{title}</h2>
      </div>
      {actions && <div className="flex gap-3">{actions}</div>}
    </div>
  )
}

export function StarRating({ rating }: { rating: number | null | undefined }) {
  // FRIDGE-1485: no rating, no stars — never a fabricated figure.
  if (typeof rating !== 'number' || !Number.isFinite(rating)) return null
  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {/* FRIDGE-179: shared <symbol>, not a path copy per star. */}
        {[...Array(5)].map((_, i) => (
          <StarIcon
            key={i}
            className={`w-3 h-3 ${i < Math.floor(rating) ? "text-[#FFA41C]" : "text-gray-200"}`}
          />
        ))}
      </div>
      <span className="text-[10px] text-black">{rating.toFixed(1)}</span>
    </div>
  )
}

export function ScrollCarousel({ eyebrow, title, bgClass = "bg-white", children }: { eyebrow: string; title: string; bgClass?: string; children: React.ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const scroll = (dir: "left" | "right") => {
    scrollRef.current?.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" })
  }
  return (
    <section className={`${bgClass} py-12`}>
      <div className={PAGE_W}>
        <PageSectionHeader
          eyebrow={eyebrow}
          title={title}
          actions={
            <>
              {/* FRIDGE-149 §1 — named prev/next, matching the shared brand-kit carousels. */}
              <button type="button" onClick={() => scroll("left")} aria-label="Scroll left" className="w-10 h-10 flex items-center justify-center rounded-lg bg-white hover:bg-gray-100 transition-colors shadow-sm border border-gray-100">
                <ChevronLeft className="w-5 h-5 text-[#ff9951]" aria-hidden="true" />
              </button>
              <button type="button" onClick={() => scroll("right")} aria-label="Scroll right" className="w-10 h-10 flex items-center justify-center rounded-lg bg-white hover:bg-gray-100 transition-colors shadow-sm border border-gray-100">
                <ChevronRight className="w-5 h-5 text-[#ff9951]" aria-hidden="true" />
              </button>
            </>
          }
        />
        <div
          ref={scrollRef}
          className="flex overflow-x-auto gap-6 snap-x snap-mandatory pb-4"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
        >
          {children}
        </div>
      </div>
    </section>
  )
}
